import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { LoginRequest, RegisterRequest } from '@wf/shared'
import { api } from '../api/client'
import { clearApiCache } from '../offline/swCache'
import { unsubscribePush } from './push'

/** Aktueller Anmeldestatus (current-user + Rolle) — die Single Source of Truth ist die status-Query. */
export function useAuth() {
  const query = useQuery({ queryKey: ['status'], queryFn: api.status })
  return {
    isLoading: query.isLoading,
    user: query.data?.user ?? null,
    needsSetup: query.data?.needsSetup ?? false,
    /** Support-Modus: der Betreiber arbeitet gerade als diese Werkstatt (Banner + Werkstatt-Navigation). */
    impersonating: query.data?.impersonatingWorkshop ?? null,
    /** Abo-Zugriffsampel der Werkstatt (alle Rollen) — speist das globale Banner + den Nur-Lese-Hinweis. */
    access: query.data?.access ?? null,
  }
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: LoginRequest) => api.login(req),
    // Nur bei echter Session den Auth-Status neu laden. Eine 2FA-Aufforderung (needTotp) ist
    // noch keine Anmeldung → status NICHT invalidieren, sonst flackert die Login-Seite.
    onSuccess: (res) => {
      if ('user' in res) {
        clearApiCache() // Kontowechsel: offline nie den Datenstand des vorigen Kontos zeigen
        void qc.invalidateQueries({ queryKey: ['status'] })
      }
    },
  })
}

/** Werkstatt-Selbstregistrierung (Admin/Chef): legt das Konto an, loggt aber NICHT ein — Antwort ist needEmailVerify.
 *  Der Login erfolgt erst nach Code-Verifizierung (useVerifyEmail). Daher hier KEINE status-Invalidierung. */
export function useRegister() {
  return useMutation({ mutationFn: (req: RegisterRequest) => api.register(req) })
}

export function useLogout() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async () => {
      await unsubscribePush() // Server-Zuordnung dieses Geräts lösen, solange die Session noch gilt (best-effort)
      return api.logout()
    },
    onSuccess: () => {
      qc.clear()
      clearApiCache() // gecachte (personenbezogene) API-Antworten gehören zur Session
      navigate('/login', { replace: true })
    },
  })
}
